// See: backend/docs/driver-scheduler.md §11
//
// Bir SHIFT veya TRANSFER PersonelService için aday sürücüleri listeler ve
// kullanıcının seçtiği sürücüyü PATCH /personelservices/:id ile servise atar.
//
// Bu modal, AutoAssignButton tarafından mount edilir. Eligibility kontrolü
// AutoAssignButton içinde yapılır — buraya geldiğinde servis zaten kapsamdadır.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";

import useDriverSchedulerApi, { ASSIGNMENT_SOURCES } from "./hooks/useDriverSchedulerApi";
import { routeTypeLabel } from "./scheduleLabels";

import "./AutoAssignSuggestionModal.css";

const formatPrevTripSuffix = (reasoning, t) => {
  const parts = [];
  if (reasoning.prevTripEndTime) parts.push(reasoning.prevTripEndTime);
  const typeLabel = routeTypeLabel(reasoning.prevTripRouteType, t);
  if (typeLabel) parts.push(typeLabel);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
};

const renderCost = (n) => (typeof n === "number" ? n.toFixed(2) : "-");

const renderNumber = (n, suffix = "") => {
  if (typeof n !== "number") return "-";
  if (Math.abs(n) < 10) return `${n.toFixed(1)}${suffix}`;
  return `${Math.round(n)}${suffix}`;
};

// Modaldaki sıralama SADECE maliyet skoruna göredir (kategori önceliği YOK). Backend'in
// kategori-öncelikli sıralaması korunur; burada operatöre en düşük maliyetli aday en üstte
// gösterilir ki "evden mi yoksa önceki bacaktan mı gelen sürücüyü seçeyim" kararını kendisi
// versin. Not: backend hangi 10 adayı döndüreceğini hâlâ kendi mantığıyla seçer.
const compareCandidatesByCost = (a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity);

/**
 * Adayları maliyet sırasına göre sıralar. Mevcut sürücü ayrıca öne çekilmez; skoru
 * listede nereye denk geliyorsa oraya oturur (en iyiyse 1., ilk 10'a girmiyorsa en altta).
 * Infeasible adaylar (ör. pasif hâle gelmiş mevcut sürücü) sırasızca listenin en altında.
 *
 * "Mevcut sürücü" tespiti iki kaynaktan yapılır (biri boş kalsa bile kart işaretlensin diye):
 *   1) aday üzerindeki `isCurrentlyAssigned` bayrağı (backend skorlama sırasında set eder)
 *   2) servisin `currentDriverIds` listesi (backend'in otoriter kaynağı)
 */
const orderCandidatesForDisplay = (candidates, currentDriverIds = []) => {
  if (!candidates?.length) return [];

  const currentIdSet = new Set((currentDriverIds || []).map(String));
  const isCurrent = (c) => c.isCurrentlyAssigned === true || currentIdSet.has(String(c.driver?.id));
  // Bayrağı normalize et → CandidateRow her iki tespit kaynağında da "mevcut" göstersin.
  const normalize = (c) => (isCurrent(c) ? { ...c, isCurrentlyAssigned: true } : c);

  const feasible = candidates.filter((c) => !c.infeasible).sort(compareCandidatesByCost);
  const infeasible = candidates.filter((c) => c.infeasible);

  let rank = 0;
  return [
    ...feasible.map((c) => ({ candidate: normalize(c), rank: ++rank })),
    ...infeasible.map((c) => ({ candidate: normalize(c), rank: null })),
  ];
};

const InfeasibleSummary = ({ infeasibleCount, infeasibleReasons, t }) => {
  if (!infeasibleCount || infeasibleCount <= 0) return null;
  const entries = Object.entries(infeasibleReasons || {});
  return (
    <div className="driver-scheduler-modal__infeasible">
      <h4>{t("driverScheduler.infeasibleSummary", { count: infeasibleCount })}</h4>
      <ul>
        {entries.map(([reason, count]) => (
          <li key={reason}>
            {t(`driverScheduler.infeasibleReason.${reason}`, { defaultValue: reason })}: {count}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CandidateRow = ({ candidate, rank, onAssign, isAssigning, t }) => {
  const { driver, category, cost, reasoning = {}, isCurrentlyAssigned, infeasible } = candidate;

  return (
    <div
      className={`driver-scheduler-candidate${
        isCurrentlyAssigned ? " driver-scheduler-candidate--current" : ""
      }${rank === 1 && !isCurrentlyAssigned ? " driver-scheduler-candidate--top" : ""}`}
    >
      <div
        className={`driver-scheduler-candidate__badge ${
          category ? `driver-scheduler-candidate__badge--${category}` : "driver-scheduler-candidate__badge--CURRENT"
        }`}
      >
        {category
          ? t(`driverScheduler.category.${category}`, { defaultValue: category })
          : t("driverScheduler.currentDriver")}
      </div>

      <div className="driver-scheduler-candidate__body">
        <div className="driver-scheduler-candidate__head">
          {typeof rank === "number" && (
            <span
              className={`driver-scheduler-candidate__rank${
                rank === 1 ? " driver-scheduler-candidate__rank--first" : ""
              }`}
              aria-label={t("driverScheduler.rankLabel", { rank })}
            >
              {rank}
            </span>
          )}
          <span className="driver-scheduler-candidate__name">{driver?.name || "-"}</span>
          {isCurrentlyAssigned && (
            <span className="driver-scheduler-candidate__current-tag">{t("driverScheduler.currentDriver")}</span>
          )}
          {driver?.phonenumber && <span className="driver-scheduler-candidate__phone">{driver.phonenumber}</span>}
        </div>

        <div className="driver-scheduler-candidate__reasoning">
          {infeasible && reasoning.infeasibleReason && (
            <span className="driver-scheduler-candidate__rest-warning">
              {t("driverScheduler.currentDriverInfeasible")}:{" "}
              {t(`driverScheduler.infeasibleReason.${reasoning.infeasibleReason}`, {
                defaultValue: reasoning.infeasibleReason,
              })}
            </span>
          )}
          {category === "PROXIMITY" && (
            <>
              {reasoning.prevTripName && (
                <span>
                  {t("driverScheduler.prevTrip")}: <strong>{reasoning.prevTripName}</strong>
                  {formatPrevTripSuffix(reasoning, t)}
                </span>
              )}
              {typeof reasoning.deadheadKm === "number" && (
                <span>
                  {t("driverScheduler.deadhead")}: <strong>{renderNumber(reasoning.deadheadKm, " km")}</strong>
                  {typeof reasoning.deadheadMin === "number" ? ` / ${renderNumber(reasoning.deadheadMin, " dk")}` : ""}
                </span>
              )}
              {typeof reasoning.idleMin === "number" && (
                <span>
                  {t("driverScheduler.idle")}: <strong>{renderNumber(reasoning.idleMin, " dk")}</strong>
                </span>
              )}
              {typeof reasoning.driverTripsThatDay === "number" && reasoning.driverTripsThatDay > 0 && (
                <span>
                  {t("driverScheduler.tripsThatDay")}: <strong>{reasoning.driverTripsThatDay}</strong>
                </span>
              )}
            </>
          )}

          {category === "HOME_PROXIMITY" && (
            <>
              <span>{t("driverScheduler.reasoning.fromHome")}</span>
              {typeof reasoning.homeDeadheadKm === "number" && (
                <span>
                  {t("driverScheduler.deadhead")}: <strong>{renderNumber(reasoning.homeDeadheadKm, " km")}</strong>
                  {typeof reasoning.homeDeadheadMin === "number"
                    ? ` / ${renderNumber(reasoning.homeDeadheadMin, " dk")}`
                    : ""}
                </span>
              )}
              {typeof reasoning.driverTripsThatDay === "number" && reasoning.driverTripsThatDay > 0 && (
                <span>
                  {t("driverScheduler.tripsThatDay")}: <strong>{reasoning.driverTripsThatDay}</strong>
                </span>
              )}
            </>
          )}

          {category === "FAIRNESS" && (
            <>
              <span>
                {t("driverScheduler.lastAssignedDate")}: <strong>{reasoning.lastAssignedDate || "-"}</strong>
              </span>
              {typeof reasoning.daysSinceLastAssignment === "number" && (
                <span>
                  {t("driverScheduler.daysSinceLastAssignment")}: <strong>{reasoning.daysSinceLastAssignment}</strong>
                </span>
              )}
              {typeof reasoning.driverTripsThatDay === "number" && (
                <span>
                  {t("driverScheduler.tripsThatDay")}: <strong>{reasoning.driverTripsThatDay}</strong>
                </span>
              )}
            </>
          )}

          {typeof reasoning.restPenalty === "number" && reasoning.restPenalty > 0 && (
            <span className="driver-scheduler-candidate__rest-warning">
              {t("driverScheduler.reasoning.restWarning")}
              {typeof reasoning.restMinBefore === "number"
                ? ` (${renderNumber(reasoning.restMinBefore, " dk")})`
                : typeof reasoning.restMinAfter === "number"
                  ? ` (${renderNumber(reasoning.restMinAfter, " dk")})`
                  : ""}
            </span>
          )}
          {typeof cost === "number" && (
            <span className="driver-scheduler-candidate__cost-meta" title={t("driverScheduler.costHint")}>
              {t("driverScheduler.optimizationCost")}: <strong>{renderCost(cost)}</strong>
            </span>
          )}
        </div>
      </div>

      {!isCurrentlyAssigned && (
        <div className="driver-scheduler-candidate__action">
          <Button type="button" size="small" disabled={isAssigning || infeasible} onClick={() => onAssign(driver.id)}>
            {t("driverScheduler.assignButton")}
          </Button>
        </div>
      )}
    </div>
  );
};

const AutoAssignSuggestionModal = ({ show, onClose, serviceId, serviceName, onAssigned }) => {
  const { t } = useTranslation();
  const { isLoading, getCandidates, assignDriver } = useDriverSchedulerApi();

  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [assigningDriverId, setAssigningDriverId] = useState(null);
  const [lockOnAssign, setLockOnAssign] = useState(false);

  const orderedCandidates = useMemo(
    () => orderCandidatesForDisplay(data?.candidates, data?.service?.currentDriverIds),
    [data?.candidates, data?.service?.currentDriverIds],
  );

  const fetchCandidates = useCallback(async () => {
    setErrorMessage(null);
    setData(null);
    try {
      const result = await getCandidates(serviceId, { max: 10 });
      setData(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.fetchError"));
    }
  }, [getCandidates, serviceId, t]);

  useEffect(() => {
    if (show && serviceId) {
      fetchCandidates();
    }
    if (!show) {
      setData(null);
      setErrorMessage(null);
      setAssigningDriverId(null);
      setLockOnAssign(false);
    }
  }, [show, serviceId, fetchCandidates]);

  const handleAssign = useCallback(
    async (driverId) => {
      if (!driverId) return;
      setAssigningDriverId(driverId);
      try {
        const candidate = data?.candidates?.find((c) => c.driver?.id === driverId);
        const updated = await assignDriver(serviceId, driverId, {
          source: ASSIGNMENT_SOURCES.AUTO_INSERT,
          lock: lockOnAssign,
          cost: typeof candidate?.cost === "number" ? candidate.cost : undefined,
        });
        if (!updated) {
          throw new Error(t("driverScheduler.assignFailed"));
        }
        const driverName = candidate?.driver?.name || "";
        window?.toast?.success?.(t("driverScheduler.assignSuccess", { name: driverName }));
        if (typeof onAssigned === "function") {
          onAssigned(updated, driverId);
        }
        onClose?.();
      } catch (err) {
        const msg = err?.message || t("driverScheduler.assignFailed");
        window?.toast?.error?.(msg);
      } finally {
        setAssigningDriverId(null);
      }
    },
    [assignDriver, data, onAssigned, onClose, serviceId, t, lockOnAssign],
  );

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onClose={onClose}
      className="driver-scheduler-modal"
      header={t("driverScheduler.autoAssignTitle")}
      contentClass="driver-scheduler-modal__content"
      footerClass="driver-scheduler-modal__footer"
      footer={
        <>
          <Button type="button" inverse onClick={onClose}>
            {t("close")}
          </Button>
        </>
      }
    >
      <div className="driver-scheduler-modal__intro">
        <strong>{serviceName || "-"}</strong>
        {data?.trip && (
          <>
            {" — "}
            {data.trip.date} • {data.trip.startTime} → {data.trip.endTime}
            {data.trip.routeType ? ` • ${routeTypeLabel(data.trip.routeType, t)}` : ""}
          </>
        )}
        {/* <label className="driver-scheduler-modal__lock-toggle">
          <input
            type="checkbox"
            checked={lockOnAssign}
            onChange={(e) => setLockOnAssign(e.target.checked)}
          />
          <span>{t("driverScheduler.lockOnAssign")}</span>
          <span className="driver-scheduler-modal__lock-hint">
            {t("driverScheduler.lockOnAssignHint")}
          </span>
        </label> */}
        {data?.candidates?.length > 0 && (
          <p className="driver-scheduler-modal__list-hint">{t("driverScheduler.candidatesListHint")}</p>
        )}
      </div>

      {isLoading && !data && <div className="driver-scheduler-modal__loading">{t("driverScheduler.loading")}</div>}

      {errorMessage && (
        <div className="driver-scheduler-modal__empty" style={{ color: "#c62828" }}>
          {errorMessage}
        </div>
      )}

      {data && !errorMessage && (
        <>
          {(!data.candidates || data.candidates.length === 0) && (
            <div className="driver-scheduler-modal__empty">{t("driverScheduler.noCandidates")}</div>
          )}

          {orderedCandidates.length > 0 && (
            <div className="driver-scheduler-modal__list">
              {orderedCandidates.map(({ candidate, rank }) => (
                <CandidateRow
                  key={candidate.driver.id}
                  candidate={candidate}
                  rank={rank}
                  onAssign={handleAssign}
                  isAssigning={assigningDriverId === candidate.driver.id}
                  t={t}
                />
              ))}
            </div>
          )}

          <InfeasibleSummary infeasibleCount={data.infeasibleCount} infeasibleReasons={data.infeasibleReasons} t={t} />
        </>
      )}
    </Modal>
  );
};

export default AutoAssignSuggestionModal;
